import { SplashScreenComponent } from '@/components/organisms/SplashScreen';
import { getUnauthenticatedLandingPath } from '@/core/constants/appConfig';
import { useAuth } from '@/core/contexts/AuthContext';
import { logger } from '@/core/logger';
import { passcodeStorage } from '@/core/storage/passcodeStorage';
import { useAppStore } from '@/core/stores/appStore';
import { getUaePassRegistrationRoute, isUaePassRegistrationOngoing } from '@/modules/onboarding/identity/uae-pass/utils/routing';
import { RegistrationStorage } from '@/modules/onboarding/registration/storage/registrationStorage';
import { getManualRegistrationRoute, isManualRegistrationOngoing } from '@/modules/onboarding/registration/utils/routing';
import { usePasscode } from '@/modules/passcode';
import { useSplash } from '@/modules/splash';
import { useRouter } from 'expo-router';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Platform } from 'react-native';

export default function SplashRoute() {
    const { isAuthenticated, isLoading } = useAuth();
    const router = useRouter();
    const hasNavigated = useRef(false);
    // Keep 3s minimum splash duration across platforms
    const { isReady } = useSplash(3000);
    const { checkPasscodeState } = usePasscode();
    const [passcodeSet, setPasscodeSet] = useState<boolean | null>(null);
    const [setupRequired, setSetupRequired] = useState<boolean | null>(null);
    const [hasReachedRegistrationSuccess, setHasReachedRegistrationSuccess] = useState<boolean | null>(null);

    // Check if passcode is set
    useEffect(() => {
        const checkPasscode = async () => {
            try {
                const isSet = await checkPasscodeState();
                logger.info('Splash:Passcode check result', { isSet });
                setPasscodeSet(isSet);
            } catch (error) {
                logger.error('Splash:Error checking passcode', error);
                setPasscodeSet(false);
            }
        };
        checkPasscode();
    }, [checkPasscodeState]);

    // Check if passcode setup is required (from login response)
    useEffect(() => {
        const checkSetupRequired = async () => {
            try {
                const isRequired = await passcodeStorage.isSetupRequired();
                logger.info('Splash:Setup required check result', { isRequired });
                setSetupRequired(isRequired);
            } catch (error) {
                logger.error('Splash:Error checking setup required', error);
                setSetupRequired(false);
            }
        };
        checkSetupRequired();
    }, []);

    // Check if user has reached registration success
    useEffect(() => {
        const checkRegistrationSuccess = async () => {
            try {
                const hasReached = await RegistrationStorage.hasReachedRegistrationSuccess();
                logger.info('Splash:Registration success check result', { hasReached });
                setHasReachedRegistrationSuccess(hasReached);
            } catch (error) {
                logger.error('Splash:Error checking registration success', error);
                setHasReachedRegistrationSuccess(false);
            }
        };
        checkRegistrationSuccess();
    }, []);

    // Determine the target route based on current state
    const getTargetRoute = useCallback(async (): Promise<string | null> => {
        // Priority 0.5: On web, if authenticated, skip passcode check and go directly to home
        // This is because web auto-skips passcode after login (handled in HomeScreen)
        // Also check if we already have a passcodeToken (skip passcode was already called)
        if (Platform.OS === 'web' && isAuthenticated) {
            const authState = useAppStore.getState().auth;
            // If we have a passcodeToken, skip passcode was already called, go to home
            // If we don't have a passcodeToken but we're on web, still go to home and let HomeScreen handle auto-skip
            logger.info('Splash:Web platform with authentication, skipping passcode check and routing to home', {
                hasPasscodeToken: !!authState.passcodeToken,
            });
            return '/(auth)/(home)';
        }

        // Priority 1: Passcode is set → always show passcode login (highest priority)
        // Skip this on web if authenticated (handled above)
        if (passcodeSet) {
            return '/passcode-login';
        }

        // Priority 1.5: Passcode setup required (from login response) → show set passcode screen
        // This handles the case where user logged in but didn't complete passcode setup
        if (setupRequired && isAuthenticated) {
            logger.info('Splash:Passcode setup required, routing to passcode setup');
            return '/passcode?from=login&mode=setup';
        }

        // Priority 2: User has reached registration success → registration-success
        // Check this BEFORE checking if registration is ongoing, to prevent routing back to OTP
        if (hasReachedRegistrationSuccess && !isAuthenticated && !passcodeSet) {
            return '/registration-success';
        }

        // Priority 3: Check if manual registration is ongoing (skip on web)
        if (Platform.OS !== 'web' && isManualRegistrationOngoing() && !isAuthenticated) {
            const route = await getManualRegistrationRoute();
            if (route) {
                logger.info('Splash:Manual registration ongoing, routing to', { route });
                return route;
            }
        }

        // Priority 4: Check if UAE Pass registration is ongoing
        if (isUaePassRegistrationOngoing() && !isAuthenticated) {
            const route = await getUaePassRegistrationRoute();
            if (route) {
                logger.info('Splash:UAE Pass registration ongoing, routing to', { route });
                return route;
            }
        }

        // Priority 5: Authenticated → home
        if (isAuthenticated) {
            return '/(auth)/(home)';
        }

        // Priority 6: Default → welcome, or Bloom Universal when landing flag is on
        return getUnauthenticatedLandingPath();
    }, [passcodeSet, setupRequired, isAuthenticated, hasReachedRegistrationSuccess]);

    // Navigate only when auth is hydrated and init (min 3s + tasks) is ready
    useEffect(() => {
        if (hasNavigated.current) return;
        if (isLoading) return;
        if (!isReady) return;
        if (passcodeSet === null) return; // Wait for passcode check
        if (setupRequired === null) return; // Wait for setup required check
        if (hasReachedRegistrationSuccess === null) return; // Wait for registration success check

        hasNavigated.current = true;

        getTargetRoute().then((targetRoute) => {
            logger.info('Splash:Navigation decision', {
                isAuthenticated,
                passcodeSet,
                setupRequired,
                hasReachedRegistrationSuccess,
                isManualRegistrationOngoing: isManualRegistrationOngoing(),
                isUaePassRegistrationOngoing: isUaePassRegistrationOngoing(),
                targetRoute,
            });

            if (targetRoute) {
                router.replace(targetRoute as any);
            } else {
                // Fallback to configured guest landing if no route determined
                logger.warn('Splash:No route determined, defaulting to guest landing');
                router.replace(getUnauthenticatedLandingPath() as any);
            }
        });
    }, [isLoading, isReady, getTargetRoute, router, passcodeSet, setupRequired, hasReachedRegistrationSuccess, isAuthenticated]);

    // Show full splash screen with video on all platforms
    return <SplashScreenComponent onVideoComplete={() => { /* keep playing; gate handled in hook */ }} />;
}
